import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import SEO from '../components/SEO';
import './CustomerAuth.css';

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { resetPassword } = useCustomerAuth();
  const [searchParams] = useSearchParams();

  const resetToken = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(t('auth.passwordMinLength', 'Password must be at least 6 characters'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDontMatch', 'Passwords do not match'));
      return;
    }

    setLoading(true);

    const result = await resetPassword(resetToken, password);

    if (result.success) {
      // If the backend returned a JWT the context already stored it and fetched the profile.
      // Navigate directly to /account (no second login needed).
      // If no token (shouldn't happen with current backend), fall back to /login.
      if (result.token) {
        navigate('/account');
      } else {
        navigate('/login', {
          state: { message: t('auth.passwordResetSuccess', 'Password has been reset. You can now log in.') }
        });
      }
    } else {
      // Always show a translated error — the raw API error is English only.
      // A 400 from reset-password always means the token is invalid or expired.
      setError(t('auth.invalidLinkMessage', 'This password reset link is invalid or has expired.'));
    }

    setLoading(false);
  };

  if (!resetToken) {
    return (
      <div className="customer-auth-page">
        <SEO title={t('auth.resetPassword', 'Reset Password')} />
        <div className="auth-card">
          <div className="auth-header">
            <h1>{t('auth.invalidLink', 'Invalid Link')}</h1>
            <p>{t('auth.invalidLinkMessage', 'This password reset link is invalid or has expired.')}</p>
          </div>
          <div className="auth-links">
            <Link to="/forgot-password">{t('auth.requestNewLink', 'Request a new link')}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-auth-page">
      <SEO title={t('auth.setNewPassword', 'Set New Password')} />

      <div className="auth-card">
        <div className="auth-header">
          <h1>{t('auth.setNewPassword', 'Set New Password')}</h1>
          <p>{t('auth.enterNewPassword', 'Choose a new password for your account')}</p>
        </div>

        {error && (
          <div className="auth-error-banner">
            {error}
            {' '}
            <Link to="/forgot-password">{t('auth.requestNewLink', 'Request a new link')}</Link>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-form-group">
            <label htmlFor="password">{t('auth.newPassword', 'New Password')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="********"
              required
              minLength={6}
              autoFocus
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="confirmPassword">{t('auth.confirmPassword', 'Confirm Password')}</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="********"
              required
              minLength={6}
            />
          </div>

          <button type="submit" className="auth-submit-btn" disabled={loading}>
            {loading ? t('auth.resetting', 'Resetting...') : t('auth.resetPasswordBtn', 'Reset Password')}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/login">{t('auth.backToLogin', 'Back to Login')}</Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
